import { PACKED_RULE_INDEX, PACKED_UNASSIGNED, RULE_FLAG_PONGDANG_PATTERNS, SHIFT_FLAG_NIGHT, SHIFT_FLAG_OFF, SHIFT_PATTERN, SHIFT_PATTERN_MASK, SHIFT_PATTERN_SHIFT, } from "./types.js";
export const GPU_SHIFT_BALANCE_WEIGHT = 80;
export const GPU_OFF_BALANCE_WEIGHT = 35;
export const GPU_PONGDANG_PATTERN_WEIGHT = 80;
const FORBIDDEN_PATTERN_WEIGHT = 10;
const AVOIDANCE_PATTERN_WEIGHT = 5;
const CONSECUTIVE_WORK_WEIGHT = 10;
const CONSECUTIVE_NIGHT_WEIGHT = 20;
const U32_MAX = 4294967295;
export function estimateMaxPackedScoreScaled(input) {
    const nurseCount = input.nurseCount;
    const dayCount = input.dayCount;
    const scoreScale = nurseCount * nurseCount;
    const activeShiftCount = input.shiftFlags.reduce((count, flags) => count + ((flags & SHIFT_FLAG_OFF) === 0 ? 1 : 0), 0);
    const consecutiveWork = nurseCount *
        Math.max(0, dayCount - input.rules[PACKED_RULE_INDEX.MAX_CONSECUTIVE_DAYS]) *
        CONSECUTIVE_WORK_WEIGHT;
    const consecutiveNight = nurseCount *
        Math.max(0, dayCount - input.rules[PACKED_RULE_INDEX.MAX_CONSECUTIVE_NIGHTS]) *
        CONSECUTIVE_NIGHT_WEIGHT;
    const forbidden = nurseCount * Math.max(0, dayCount - 1) * FORBIDDEN_PATTERN_WEIGHT;
    const avoidance = nurseCount * Math.max(0, dayCount - 2) * AVOIDANCE_PATTERN_WEIGHT;
    const pongdang = nurseCount * Math.max(0, dayCount - 3) * GPU_PONGDANG_PATTERN_WEIGHT;
    const maximumBalanceNumerator = Math.max(0, nurseCount - 1) * dayCount * dayCount;
    return ((consecutiveWork + consecutiveNight + forbidden + avoidance + pongdang) * scoreScale +
        maximumBalanceNumerator * GPU_OFF_BALANCE_WEIGHT +
        maximumBalanceNumerator * activeShiftCount * GPU_SHIFT_BALANCE_WEIGHT);
}
export function assertPackedScoreFitsU32(input) {
    const scoreScale = input.nurseCount * input.nurseCount;
    if (!Number.isSafeInteger(scoreScale) || scoreScale > U32_MAX) {
        throw new RangeError(`입력의 score scale이 u32 범위를 초과합니다: ${scoreScale}`);
    }
    const maximum = estimateMaxPackedScoreScaled(input);
    if (!Number.isSafeInteger(maximum) || maximum > U32_MAX) {
        throw new RangeError(`입력의 최악 score가 u32 범위를 초과할 수 있습니다: ${maximum}`);
    }
}
function patternCode(input, shiftIndex) {
    if (shiftIndex === PACKED_UNASSIGNED)
        return SHIFT_PATTERN.OFF;
    return (input.shiftFlags[shiftIndex] & SHIFT_PATTERN_MASK) >>> SHIFT_PATTERN_SHIFT;
}
function isOff(input, shiftIndex) {
    return shiftIndex !== PACKED_UNASSIGNED && (input.shiftFlags[shiftIndex] & SHIFT_FLAG_OFF) !== 0;
}
function isNight(input, shiftIndex) {
    return (shiftIndex !== PACKED_UNASSIGNED && (input.shiftFlags[shiftIndex] & SHIFT_FLAG_NIGHT) !== 0);
}
function isActive(input, shiftIndex) {
    return shiftIndex !== PACKED_UNASSIGNED && !isOff(input, shiftIndex);
}
function isForbidden(previous, current) {
    return ((previous === SHIFT_PATTERN.NIGHT &&
        (current === SHIFT_PATTERN.EVENING || current === SHIFT_PATTERN.DAY)) ||
        (previous === SHIFT_PATTERN.EVENING && current === SHIFT_PATTERN.DAY));
}
function isAvoidance(first, second, third) {
    return ((first === SHIFT_PATTERN.NIGHT &&
        second === SHIFT_PATTERN.NIGHT &&
        third === SHIFT_PATTERN.NIGHT) ||
        (first === SHIFT_PATTERN.NIGHT &&
            second === SHIFT_PATTERN.OFF &&
            (third === SHIFT_PATTERN.DAY || third === SHIFT_PATTERN.EVENING)));
}
function assertCandidateShape(input, candidate) {
    const expectedLength = input.dayCount * input.nurseCount;
    if (candidate.assignments.length !== expectedLength) {
        throw new RangeError(`후보 assignment 길이가 올바르지 않습니다. expected=${expectedLength}, actual=${candidate.assignments.length}`);
    }
}
function calculateLegacyVariance(counts) {
    if (counts.length === 0)
        return 0;
    const total = counts.reduce((sum, count) => sum + count, 0);
    const mean = total / counts.length;
    return counts.reduce((sum, count) => sum + (count - mean) ** 2, 0) / counts.length;
}
/**
 * GPU 생성 정책의 7개 벌점을 Packed assignment에서 계산한다.
 *
 * 균형 점수의 분산은 `N * sum(x²) - sum(x)²` 정수 numerator를 사용하고,
 * 전체 점수는 `N²` 배율의 u32로 만든다. CPU 자동 생성은 이 정책을 사용하지
 * 않으며, 이 값이 GPU WGSL과 GPU 검증용 기준 구현의 비교 기준이다.
 */
export function evaluatePackedCandidate(input, candidate) {
    return evaluateNurses(input, candidate, null);
}
/** Pair contribution for an exchange; retains original day-major assignment strides. */
export function evaluatePackedNursePair(input, candidate, first, second) {
    if (!Number.isSafeInteger(first) ||
        !Number.isSafeInteger(second) ||
        first < 0 ||
        second < 0 ||
        first >= input.nurseCount ||
        second >= input.nurseCount ||
        first === second)
        throw new RangeError("Invalid nurse pair");
    return evaluateNurses(input, candidate, [first, second]);
}
function evaluateNurses(input, candidate, selected) {
    assertCandidateShape(input, candidate);
    const nurseCount = selected?.length ?? input.nurseCount;
    const shiftCount = input.shiftCount;
    const consecutiveWorkCounts = new Uint32Array(nurseCount);
    const consecutiveNightCounts = new Uint32Array(nurseCount);
    const offCounts = new Uint32Array(nurseCount);
    const shiftCounts = new Uint32Array(nurseCount * shiftCount);
    const previousPattern = new Uint32Array(nurseCount);
    const patternHistory = Array.from({ length: nurseCount }, () => []);
    const firstSeen = new Uint8Array(nurseCount);
    previousPattern.fill(SHIFT_PATTERN.OFF);
    let consecutiveWork = 0;
    let consecutiveNight = 0;
    let forbiddenPatterns = 0;
    let avoidancePatterns = 0;
    let pongdangPatterns = 0;
    const maxConsecutiveDays = input.rules[PACKED_RULE_INDEX.MAX_CONSECUTIVE_DAYS];
    const maxConsecutiveNights = input.rules[PACKED_RULE_INDEX.MAX_CONSECUTIVE_NIGHTS] || 3;
    const usePongdang = (input.rules[PACKED_RULE_INDEX.FLAGS] & RULE_FLAG_PONGDANG_PATTERNS) !== 0;
    for (let dayIndex = 0; dayIndex < input.dayCount; dayIndex++) {
        for (let nurseIndex = 0; nurseIndex < nurseCount; nurseIndex++) {
            const assignment = candidate.assignments[dayIndex * input.nurseCount + (selected?.[nurseIndex] ?? nurseIndex)];
            if (assignment !== PACKED_UNASSIGNED && assignment >= shiftCount) {
                throw new RangeError(`후보에 범위를 벗어난 Shift index가 있습니다: ${assignment}`);
            }
            const active = isActive(input, assignment);
            const night = isNight(input, assignment);
            const off = isOff(input, assignment);
            const currentPattern = patternCode(input, assignment);
            if (active) {
                consecutiveWorkCounts[nurseIndex]++;
                if (consecutiveWorkCounts[nurseIndex] > maxConsecutiveDays) {
                    consecutiveWork += CONSECUTIVE_WORK_WEIGHT;
                }
            }
            else {
                consecutiveWorkCounts[nurseIndex] = 0;
            }
            if (night) {
                consecutiveNightCounts[nurseIndex]++;
                if (consecutiveNightCounts[nurseIndex] > maxConsecutiveNights) {
                    consecutiveNight += CONSECUTIVE_NIGHT_WEIGHT;
                }
            }
            else {
                consecutiveNightCounts[nurseIndex] = 0;
            }
            if (off)
                offCounts[nurseIndex]++;
            if (active)
                shiftCounts[nurseIndex * shiftCount + assignment]++;
            if (firstSeen[nurseIndex] !== 0 &&
                assignment !== PACKED_UNASSIGNED &&
                isForbidden(previousPattern[nurseIndex], currentPattern)) {
                forbiddenPatterns += FORBIDDEN_PATTERN_WEIGHT;
            }
            previousPattern[nurseIndex] =
                assignment === PACKED_UNASSIGNED ? SHIFT_PATTERN.OFF : currentPattern;
            if (assignment !== PACKED_UNASSIGNED || firstSeen[nurseIndex] !== 0) {
                firstSeen[nurseIndex] = 1;
                const history = patternHistory[nurseIndex];
                history.push(currentPattern);
                const length = history.length;
                if (length >= 3 &&
                    isAvoidance(history[length - 3], history[length - 2], history[length - 1])) {
                    avoidancePatterns += AVOIDANCE_PATTERN_WEIGHT;
                }
                if (usePongdang &&
                    length >= 4 &&
                    history[length - 4] === SHIFT_PATTERN.NIGHT &&
                    history[length - 3] === SHIFT_PATTERN.OFF &&
                    history[length - 2] === SHIFT_PATTERN.NIGHT &&
                    history[length - 1] === SHIFT_PATTERN.OFF) {
                    pongdangPatterns += GPU_PONGDANG_PATTERN_WEIGHT;
                }
            }
        }
    }
    let totalOff = 0;
    let sumOffSquares = 0;
    for (const count of offCounts) {
        totalOff += count;
        sumOffSquares += count * count;
    }
    const offNumerator = nurseCount * sumOffSquares - totalOff * totalOff;
    let shiftNumerator = 0;
    for (let shiftIndex = 0; shiftIndex < shiftCount; shiftIndex++) {
        if ((input.shiftFlags[shiftIndex] & SHIFT_FLAG_OFF) !== 0)
            continue;
        let total = 0;
        let sumSquares = 0;
        for (let nurseIndex = 0; nurseIndex < nurseCount; nurseIndex++) {
            const count = shiftCounts[nurseIndex * shiftCount + shiftIndex];
            total += count;
            sumSquares += count * count;
        }
        shiftNumerator += nurseCount * sumSquares - total * total;
    }
    const scoreScale = nurseCount * nurseCount;
    const offBalance = calculateLegacyVariance(offCounts) * GPU_OFF_BALANCE_WEIGHT;
    let totalShiftVariance = 0;
    for (let shiftIndex = 0; shiftIndex < shiftCount; shiftIndex++) {
        if ((input.shiftFlags[shiftIndex] & SHIFT_FLAG_OFF) !== 0)
            continue;
        const counts = new Uint32Array(nurseCount);
        for (let nurseIndex = 0; nurseIndex < nurseCount; nurseIndex++) {
            counts[nurseIndex] = shiftCounts[nurseIndex * shiftCount + shiftIndex];
        }
        totalShiftVariance += calculateLegacyVariance(counts);
    }
    const shiftBalance = totalShiftVariance * GPU_SHIFT_BALANCE_WEIGHT;
    const score = consecutiveWork +
        consecutiveNight +
        offBalance +
        shiftBalance +
        forbiddenPatterns +
        avoidancePatterns +
        pongdangPatterns;
    const scoreScaled = (consecutiveWork +
        consecutiveNight +
        forbiddenPatterns +
        avoidancePatterns +
        pongdangPatterns) *
        scoreScale +
        offNumerator * GPU_OFF_BALANCE_WEIGHT +
        shiftNumerator * GPU_SHIFT_BALANCE_WEIGHT;
    if (!Number.isSafeInteger(scoreScaled) || scoreScaled > U32_MAX) {
        throw new RangeError(`Packed score가 u32 범위를 초과합니다: ${scoreScaled}. 입력을 더 작은 청크로 제한해야 합니다.`);
    }
    return {
        candidateIndex: candidate.candidateIndex,
        score,
        scoreScaled,
        scoreScale,
        breakdown: {
            consecutiveWork,
            consecutiveNight,
            offBalance,
            shiftBalance,
            forbiddenPatterns,
            avoidancePatterns,
            pongdangPatterns,
        },
        balanceNumerators: {
            off: offNumerator,
            shift: shiftNumerator,
        },
    };
}
//# sourceMappingURL=evaluator.js.map